import { useState } from 'react';
import { FlaskConical, Beaker, Play, Download, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { generatePanel, generateLongitudinalPanel } from '../../../services/patientJourney/panelEngine';
import { savePanel } from '../../../services/firestore/pjeService';
import type {
  PanelGeneratorConfig,
  GeneratedPanel as EnginePanel,
  LongitudinalConfig,
  LabValue,
} from '../../../types/patientJourney';

// ── CONSTANTS ──

const VERTICALS = [
  "Women's Hormones", "Men's Hormones", 'Thyroid', 'Metabolic', 'Fertility', 'General Wellness',
];

/** Map UI vertical display names to engine vertical identifiers */
const VERTICAL_TO_ENGINE: Record<string, string> = {
  "Women's Hormones": 'hormones_women',
  "Men's Hormones": 'hormones_men',
  'Thyroid': 'thyroid',
  'Metabolic': 'metabolic',
  'Fertility': 'fertility',
  'General Wellness': 'longevity',
};

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
];

const HEALTH_PROFILES = ['Optimal', 'Mixed', 'Concerning', 'Critical'] as const;
type HealthProfile = typeof HEALTH_PROFILES[number];

const PROGRESSION_PATTERNS = ['improvement', 'plateau', 'worsening', 'mixed', 'new_variable'] as const;

const TARGET_STATUSES = [
  'below_functional', 'above_functional', 'below_conventional', 'above_conventional', 'optimal',
] as const;

// ── TIER CONFIG ──

interface TierConfig {
  tier: number;
  name: string;
  subtitle: string;
  countLabel: string;
  minMarkers: number;
  maxMarkers: number;
}

const TIERS: TierConfig[] = [
  { tier: 1, name: 'TIER 1', subtitle: 'Trigger', countLabel: '5-8', minMarkers: 5, maxMarkers: 8 },
  { tier: 2, name: 'TIER 2', subtitle: 'Standard', countLabel: '20-35', minMarkers: 20, maxMarkers: 35 },
  { tier: 3, name: 'TIER 3', subtitle: 'Compreh.', countLabel: '50-70', minMarkers: 50, maxMarkers: 70 },
  { tier: 4, name: 'TIER 4', subtitle: 'Func Hlth', countLabel: '100-130+', minMarkers: 100, maxMarkers: 130 },
  { tier: 5, name: 'TIER 5', subtitle: 'Longit.', countLabel: '\u00D7 2-4', minMarkers: 20, maxMarkers: 35 },
];

// ── TYPES ──

interface TriggerTarget {
  id: string;
  biomarker: string;
  targetStatus: string;
}

/** UI display type — mapped from engine LabValue */
interface GeneratedLab {
  biomarker: string;
  value: number;
  unit: string;
  convRange: string;
  funcRange: string;
  status: 'optimal' | 'above_functional' | 'below_functional' | 'above_conventional' | 'below_conventional';
}

// ── HELPERS ──

let _uid = 0;
function uid() {
  return `_pg_${++_uid}_${Date.now()}`;
}

/** Convert engine LabValue status to the UI display subset (maps 'elevated' to 'above_conventional') */
function normalizeStatus(status: LabValue['status']): GeneratedLab['status'] {
  if (status === 'elevated') return 'above_conventional';
  return status;
}

/** Map engine LabValue[] to the UI GeneratedLab[] format */
function labsToGeneratedLabs(labs: LabValue[]): GeneratedLab[] {
  return labs.map((lab) => ({
    biomarker: lab.biomarker,
    value: lab.value,
    unit: lab.unit,
    convRange: lab.conventionalRange,
    funcRange: lab.functionalRange,
    status: normalizeStatus(lab.status),
  })).sort((a, b) => a.biomarker.localeCompare(b.biomarker));
}

/** Map UI health profile label to engine config value */
function toEngineHealthProfile(hp: HealthProfile): PanelGeneratorConfig['healthProfile'] {
  return hp.toLowerCase() as PanelGeneratorConfig['healthProfile'];
}

/** Map UI vertical display names to engine vertical identifiers */
function toEngineVerticals(uiVerticals: string[]): string[] {
  return uiVerticals
    .map((v) => VERTICAL_TO_ENGINE[v])
    .filter((v): v is string => v !== undefined);
}

function getStatusPill(status: GeneratedLab['status']): { label: string; bgClass: string; textClass: string } {
  switch (status) {
    case 'optimal':
      return { label: 'Optimal', bgClass: 'bg-optimal/15', textClass: 'text-optimal' };
    case 'above_functional':
    case 'below_functional':
      return {
        label: status === 'above_functional' ? 'Above Func.' : 'Below Func.',
        bgClass: 'bg-suboptimal/15',
        textClass: 'text-suboptimal',
      };
    case 'above_conventional':
    case 'below_conventional':
      return {
        label: status === 'above_conventional' ? 'Above Conv.' : 'Below Conv.',
        bgClass: 'bg-critical/15',
        textClass: 'text-critical',
      };
  }
}

// ── MAIN COMPONENT ──

export function PanelGeneratorView() {
  const [selectedTier, setSelectedTier] = useState(2);
  const [configOpen, setConfigOpen] = useState(true);
  const [tier5Open, setTier5Open] = useState(true);

  // Configuration
  const [age, setAge] = useState('42');
  const [sex, setSex] = useState('Female');
  const [state, setState] = useState('');
  const [selectedVerticals, setSelectedVerticals] = useState<string[]>(["Women's Hormones"]);
  const [healthProfile, setHealthProfile] = useState<HealthProfile>('Mixed');
  const [seed, setSeed] = useState('');
  const [triggerTargets, setTriggerTargets] = useState<TriggerTarget[]>([]);

  // Tier 5 extras
  const [drawCount, setDrawCount] = useState('2');
  const [drawInterval, setDrawInterval] = useState('90');
  const [progressionPattern, setProgressionPattern] = useState('improvement');

  // Results
  const [generatedPanel, setGeneratedPanel] = useState<GeneratedLab[] | null>(null);
  const [engineResult, setEngineResult] = useState<EnginePanel | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Vertical toggle
  function toggleVertical(v: string) {
    setSelectedVerticals((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    );
  }

  // Trigger targets
  function addTriggerTarget() {
    setTriggerTargets((prev) => [...prev, { id: uid(), biomarker: '', targetStatus: '' }]);
  }
  function updateTriggerTarget(id: string, field: 'biomarker' | 'targetStatus', value: string) {
    setTriggerTargets((prev) =>
      prev.map((t) => (t.id === id ? { ...t, [field]: value } : t))
    );
  }
  function removeTriggerTarget(id: string) {
    setTriggerTargets((prev) => prev.filter((t) => t.id !== id));
  }

  // Generate panel using the real engine
  function handleGenerate() {
    setIsGenerating(true);

    // Use setTimeout(0) to let React flush the loading state before synchronous engine work
    setTimeout(() => {
      try {
        const parsedSeed = seed ? parseInt(seed, 10) || undefined : undefined;
        const engineVerticals = toEngineVerticals(selectedVerticals);

        const baseConfig: PanelGeneratorConfig = {
          tier: selectedTier as PanelGeneratorConfig['tier'],
          demographics: {
            name: 'Panel Generator',
            age: parseInt(age, 10) || 42,
            sex: sex as 'Male' | 'Female',
            state: state || 'CA',
          },
          verticals: engineVerticals,
          healthProfile: toEngineHealthProfile(healthProfile),
          seed: parsedSeed,
          triggerTargets: triggerTargets
            .filter((tt) => tt.biomarker && tt.targetStatus)
            .map((tt) => ({
              biomarkerId: tt.biomarker,
              targetStatus: tt.targetStatus as 'below_functional' | 'above_functional' | 'below_conventional' | 'above_conventional',
            })),
        };

        if (selectedTier === 5) {
          // Longitudinal panel
          const longConfig: LongitudinalConfig = {
            ...baseConfig,
            tier: 5,
            drawCount: parseInt(drawCount, 10) as LongitudinalConfig['drawCount'],
            drawIntervalDays: parseInt(drawInterval, 10) || 90,
            progressionPattern: progressionPattern as LongitudinalConfig['progressionPattern'],
          };

          const longResult = generateLongitudinalPanel(longConfig);

          // Flatten all draws into a single display list, using the last draw for display
          const lastDraw = longResult.draws[longResult.draws.length - 1];
          setEngineResult(lastDraw);
          setGeneratedPanel(labsToGeneratedLabs(lastDraw.labs));
        } else {
          // Standard single panel
          const result = generatePanel(baseConfig);
          setEngineResult(result);
          setGeneratedPanel(labsToGeneratedLabs(result.labs));
        }
      } catch (err) {
        console.error('Panel generation failed:', err);
      } finally {
        setIsGenerating(false);
      }
    }, 0);
  }

  // Export CSV — persist to Firestore first, then download
  async function handleExportCSV() {
    if (!generatedPanel) return;

    // Persist to Firestore if we have an engine result
    if (engineResult && !isSaving) {
      setIsSaving(true);
      try {
        await savePanel(engineResult);
      } catch (err) {
        console.error('Failed to save panel to Firestore:', err);
      } finally {
        setIsSaving(false);
      }
    }

    const headers = ['Biomarker', 'Value', 'Unit', 'Conventional Range', 'Functional Range', 'Status'];
    const rows = generatedPanel.map((lab) => [
      lab.biomarker,
      lab.value.toString(),
      lab.unit,
      lab.convRange,
      lab.funcRange,
      lab.status.replace(/_/g, ' '),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `panel_tier${selectedTier}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Summary stats
  const summaryStats = generatedPanel
    ? {
        total: generatedPanel.length,
        triggers: generatedPanel.filter(
          (l) => l.status === 'above_conventional' || l.status === 'below_conventional'
        ).length,
        suboptimal: generatedPanel.filter(
          (l) => l.status === 'above_functional' || l.status === 'below_functional'
        ).length,
        optimal: generatedPanel.filter((l) => l.status === 'optimal').length,
      }
    : null;

  return (
    <div className="max-w-6xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 rounded-lg bg-gold/10">
          <FlaskConical size={22} className="text-gold" />
        </div>
        <div>
          <h2 className="text-3xl font-display text-gold">Panel Generator</h2>
          <p className="text-base text-text-muted">Configure and generate synthetic lab panels at different tiers</p>
        </div>
      </div>

      {/* ── TIER SELECTOR ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {TIERS.map((tier) => {
          const active = selectedTier === tier.tier;
          return (
            <button
              key={tier.tier}
              onClick={() => setSelectedTier(tier.tier)}
              className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all text-center ${
                active
                  ? 'border-gold bg-gold/10 shadow-[0_0_12px_rgba(var(--gold-rgb),0.15)]'
                  : 'border-bg-tertiary bg-bg-card hover:border-gold/30'
              }`}
            >
              <span className={`text-lg font-bold font-display ${active ? 'text-gold' : 'text-text-primary'}`}>
                {tier.name}
              </span>
              <span className={`text-base ${active ? 'text-gold/80' : 'text-text-secondary'}`}>
                {tier.subtitle}
              </span>
              <span className={`text-base font-semibold mt-1 ${active ? 'text-gold' : 'text-text-muted'}`}>
                {tier.countLabel}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── CONFIGURATION CARD ── */}
      <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden mb-4">
        <button
          type="button"
          onClick={() => setConfigOpen(!configOpen)}
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-bg-elevated transition-colors"
        >
          <h3 className="text-base font-semibold text-text-secondary uppercase tracking-wider">Configuration</h3>
          {configOpen ? (
            <ChevronUp size={16} className="text-text-muted" />
          ) : (
            <ChevronDown size={16} className="text-text-muted" />
          )}
        </button>
          {configOpen && (
            <div>
              <div className="px-5 pb-5 space-y-5">
                {/* Demographics quick inputs */}
                <div>
                  <p className="text-base font-semibold text-gold mb-3">Demographics</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="Age">
                      <input
                        type="number"
                        value={age}
                        onChange={(e) => setAge(e.target.value)}
                        placeholder="42"
                        min={0}
                        max={120}
                        className="input-field"
                      />
                    </Field>
                    <Field label="Sex">
                      <select value={sex} onChange={(e) => setSex(e.target.value)} className="input-field">
                        <option value="Female">Female</option>
                        <option value="Male">Male</option>
                      </select>
                    </Field>
                    <Field label="State">
                      <select value={state} onChange={(e) => setState(e.target.value)} className="input-field">
                        <option value="">Any</option>
                        {US_STATES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </div>

                {/* Verticals */}
                <div>
                  <p className="text-base font-semibold text-gold mb-3">Verticals</p>
                  <div className="flex flex-wrap gap-2">
                    {VERTICALS.map((v) => {
                      const active = selectedVerticals.includes(v);
                      return (
                        <button
                          key={v}
                          type="button"
                          onClick={() => toggleVertical(v)}
                          className={`px-3 py-2 rounded-lg text-base border transition-colors ${
                            active
                              ? 'bg-gold/15 border-gold/40 text-gold'
                              : 'bg-bg-elevated border-bg-tertiary text-text-secondary hover:border-gold/20'
                          }`}
                        >
                          {v}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Health Profile */}
                <div>
                  <p className="text-base font-semibold text-gold mb-3">Health Profile</p>
                  <div className="flex flex-wrap gap-3">
                    {HEALTH_PROFILES.map((hp) => {
                      const active = healthProfile === hp;
                      return (
                        <label
                          key={hp}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-base cursor-pointer transition-colors ${
                            active
                              ? 'bg-gold/15 border-gold/40 text-gold'
                              : 'bg-bg-elevated border-bg-tertiary text-text-secondary hover:border-gold/20'
                          }`}
                        >
                          <input
                            type="radio"
                            name="healthProfile"
                            value={hp}
                            checked={active}
                            onChange={() => setHealthProfile(hp)}
                            className="sr-only"
                          />
                          {hp}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Seed */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Seed (optional, for reproducibility)">
                    <input
                      type="number"
                      value={seed}
                      onChange={(e) => setSeed(e.target.value)}
                      placeholder="Random"
                      className="input-field"
                    />
                  </Field>
                </div>

                {/* Trigger targets */}
                <div>
                  <p className="text-base font-semibold text-gold mb-3">Trigger Targets</p>
                  <div className="space-y-2">
                    {triggerTargets.length === 0 && (
                      <p className="text-base text-text-muted">No trigger targets set. Results will use health profile defaults.</p>
                    )}
                    {triggerTargets.map((tt) => (
                      <div
                        key={tt.id}
                        className="grid grid-cols-1 sm:grid-cols-[1fr_180px_40px] gap-2 items-end bg-bg-elevated rounded-lg p-3"
                      >
                        <Field label="Biomarker">
                          <input
                            type="text"
                            value={tt.biomarker}
                            onChange={(e) => updateTriggerTarget(tt.id, 'biomarker', e.target.value)}
                            placeholder="e.g. TSH"
                            className="input-field"
                          />
                        </Field>
                        <Field label="Target Status">
                          <select
                            value={tt.targetStatus}
                            onChange={(e) => updateTriggerTarget(tt.id, 'targetStatus', e.target.value)}
                            className="input-field"
                          >
                            <option value="">Select...</option>
                            {TARGET_STATUSES.map((s) => (
                              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                            ))}
                          </select>
                        </Field>
                        <button
                          type="button"
                          onClick={() => removeTriggerTarget(tt.id)}
                          className="p-2 rounded-lg hover:bg-bg-tertiary text-text-muted hover:text-critical transition-colors self-end"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addTriggerTarget}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-bg-elevated border border-bg-tertiary rounded-lg text-base text-text-secondary hover:border-gold/30 hover:text-gold transition-colors"
                    >
                      <Plus size={16} />
                      Add Trigger Target
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>

      {/* ── TIER 5 EXTRAS ── */}
        {selectedTier === 5 && (
          <div className="mb-4">
            <div className="bg-bg-card border border-gold/20 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setTier5Open(!tier5Open)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-bg-elevated transition-colors"
              >
                <h3 className="text-base font-semibold text-gold uppercase tracking-wider">
                  Tier 5 — Longitudinal Settings
                </h3>
                {tier5Open ? (
                  <ChevronUp size={16} className="text-gold" />
                ) : (
                  <ChevronDown size={16} className="text-gold" />
                )}
              </button>
              {tier5Open && (
                <div className="px-5 pb-5">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="Draw Count">
                      <select value={drawCount} onChange={(e) => setDrawCount(e.target.value)} className="input-field">
                        <option value="2">2 draws</option>
                        <option value="3">3 draws</option>
                        <option value="4">4 draws</option>
                      </select>
                    </Field>
                    <Field label="Draw Interval (days)">
                      <input
                        type="number"
                        value={drawInterval}
                        onChange={(e) => setDrawInterval(e.target.value)}
                        placeholder="90"
                        min={1}
                        className="input-field"
                      />
                    </Field>
                    <Field label="Progression Pattern">
                      <select
                        value={progressionPattern}
                        onChange={(e) => setProgressionPattern(e.target.value)}
                        className="input-field"
                      >
                        {PROGRESSION_PATTERNS.map((p) => (
                          <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      {/* ── GENERATE BUTTON ── */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-bg-deep rounded-lg text-base font-semibold hover:bg-gold/90 transition-colors disabled:opacity-50"
        >
          {isGenerating ? (
            <Beaker size={18} className="animate-pulse" />
          ) : (
            <Play size={18} />
          )}
          {isGenerating ? 'Generating...' : 'Generate Panel'}
        </button>
        {generatedPanel && (
          <button
            onClick={handleExportCSV}
            className="inline-flex items-center gap-2 px-5 py-3 bg-bg-elevated border border-bg-tertiary rounded-lg text-base text-text-secondary hover:border-gold/30 hover:text-gold transition-colors"
          >
            <Download size={16} />
            Download CSV
          </button>
        )}
      </div>

      {/* ── RESULTS ── */}
      {generatedPanel && (
        <div className="space-y-4">
          {/* Summary Stats */}
          {summaryStats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard label="Total Markers" value={summaryStats.total} color="text-gold" />
              <StatCard label="Critical (Conv.)" value={summaryStats.triggers} color="text-critical" />
              <StatCard label="Suboptimal (Func.)" value={summaryStats.suboptimal} color="text-suboptimal" />
              <StatCard label="Optimal" value={summaryStats.optimal} color="text-optimal" />
            </div>
          )}

          {/* Results Table */}
          <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-base">
                <thead>
                  <tr className="border-b border-bg-tertiary">
                    <th className="text-left px-4 py-3 text-text-muted font-medium">Biomarker</th>
                    <th className="text-right px-4 py-3 text-text-muted font-medium">Value</th>
                    <th className="text-left px-4 py-3 text-text-muted font-medium">Unit</th>
                    <th className="text-left px-4 py-3 text-text-muted font-medium hidden md:table-cell">Conv. Range</th>
                    <th className="text-left px-4 py-3 text-text-muted font-medium hidden lg:table-cell">Func. Range</th>
                    <th className="text-left px-4 py-3 text-text-muted font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {generatedPanel.map((lab, idx) => {
                    const pill = getStatusPill(lab.status);
                    return (
                      <tr
                        key={lab.biomarker}
                        className={`border-b border-bg-tertiary/50 ${idx % 2 === 0 ? '' : 'bg-bg-elevated/30'}`}
                      >
                        <td className="px-4 py-3 text-text-primary font-medium">{lab.biomarker}</td>
                        <td className="px-4 py-3 text-right text-text-primary tabular-nums">{lab.value}</td>
                        <td className="px-4 py-3 text-text-secondary">{lab.unit}</td>
                        <td className="px-4 py-3 text-text-muted hidden md:table-cell">{lab.convRange}</td>
                        <td className="px-4 py-3 text-text-muted hidden lg:table-cell">{lab.funcRange}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-sm font-medium ${pill.bgClass} ${pill.textClass}`}>
                            {pill.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Clinical Narrative */}
          <div className="bg-bg-card border border-bg-tertiary rounded-xl p-5">
            <p className="text-base font-semibold text-text-secondary uppercase tracking-wider mb-3">Clinical Narrative</p>
            <div className="bg-bg-elevated rounded-lg p-4 min-h-[100px]">
              {engineResult?.clinicalNarrative ? (
                <p className="text-base text-text-primary">
                  {engineResult.clinicalNarrative}
                  {selectedTier === 5 && (
                    <span className="block mt-2 text-text-muted italic">
                      Longitudinal analysis: {drawCount} draws, {drawInterval}-day intervals, {progressionPattern.replace(/_/g, ' ')} pattern.
                    </span>
                  )}
                </p>
              ) : (
                <p className="text-base text-text-muted italic">
                  Clinical narrative will be generated by the interpretation engine.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── SUB-COMPONENTS ──

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-text-muted mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-bg-card border border-bg-tertiary rounded-xl p-4 text-center">
      <p className={`text-2xl font-bold font-display ${color}`}>{value}</p>
      <p className="text-base text-text-muted mt-1">{label}</p>
    </div>
  );
}
